import { Text, View, StyleSheet, PDFViewer, Font } from "@react-pdf/renderer";
import { LoadingSpinner } from "components/LoadingSpinner";
import { orderStatusConstants, orderTypeConstants } from "constants/order";
import { useUserWithAccount } from "context/UserAccountProvider";
import { parseDate } from "helpers/date";
import { getFeeTypeForOrder } from "helpers/fee";
import {
  calculateClearCharge,
  centsToDollars,
  parsePrice,
} from "helpers/price";
import useDocumentTitle from "hooks/useDocumentTitle";
import useIsMobile from "hooks/useIsMobile";
import usePrice from "hooks/usePrice";
import { useQuery } from "hooks/useQuery";
import { sizer } from "layout/styles/styled/sizer";
import { isEmpty, sumBy } from "lodash";
import { normalizePaymentPlanBreakdown } from "pages/OrderView/components/ProviderPayoutCalculations/helpers";
import { Button } from "primereact/button";
import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useGetPublicOrderQuery } from "store/queries/order";
import { useGetCardLastDigitsQuery } from "store/queries/stripe";
import { orderSelector } from "store/slices/order";
import styled from "styled-components";
import {
  IDefaultProcedure,
  IDefaultServiceDetails,
  IOrder,
  IPatientResponsibilityProcedure,
} from "types/Order/Order";

import { MobileReceiptViewer } from "./components/MobileReceiptViewer";
import { ReceiptDefault } from "./components/ReceiptDefault";

const hospitalGeneralDefault =
  "https://assets.dev.price.clearhealthinc.com/hospital_logo.png";

const clearLogo =
  "https://assets.dev.price.clearhealthinc.com/clearhealthLogo.png";

type UniqueProcedureList = IDefaultProcedure | IPatientResponsibilityProcedure;

const StyledButton = styled(Button)`
  gap: ${sizer(2)};
  margin-bottom: ${sizer(4)};
  align-self: flex-start;

  span:first-child {
    font-size: ${sizer(3)};
  }
`;

export function Receipt({
  order,
  showBackToOrderButton = true,
}: {
  order?: IOrder;
  showBackToOrderButton?: boolean;
}) {
  useDocumentTitle("Receipt View");
  const FOOTER_H = 112;
  const GAP = 8;
  const styles = useMemo(
    () =>
      StyleSheet.create({
        page: {
          fontFamily: "Lato",
          display: "flex",
          justifyContent: "space-between",
          backgroundColor: "#f9f9fa",
          position: "relative",
          paddingTop: GAP,
          paddingBottom: FOOTER_H + GAP,
        },
        image: {
          width: "80px",
          marginTop: "4px",
          marginLeft: "16px",
        },
        pageDescription: {
          fontSize: "14px",
          margin: "10px",
          textAlign: "center",
          color: "#4f46e5",
        },
        container: {
          display: "flex",
          flexDirection: "row",
          gap: "16px",
          justifyContent: "space-between",
          margin: "5px 20px",
        },
        section: {
          fontFamily: "Lato",
          padding: "10px 15px",
          borderRadius: "10px",
          backgroundColor: "#FFF",
          border: "1px solid #D9D9D9",
          flexDirection: "column",
          width: "100%",
        },
        subsection: {
          fontFamily: "Lato",
          padding: "10px 15px",
          borderRadius: "10px",
          backgroundColor: "#FFF",
          border: "1px solid #D9D9D9",
          flexDirection: "column",
          width: "50%",
        },
        title: {
          fontSize: "13px",
          color: "#0D0D0D",
          fontWeight: 900,
          marginBottom: "12px",
        },
        titleSmall: {
          fontSize: "13px",
          color: "#0D0D0D",
          fontWeight: 800,
        },
        subtitle: {
          fontSize: "12px",
          color: "#0D0D0D",
          fontWeight: 800,
          marginBottom: "3px",
        },
        text: {
          color: "#666",
          fontWeight: 300,
          fontSize: "12px",
        },
        textSmall: {
          fontSize: "11px",
        },
        paymentItem: {
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "10px",
        },
        paymentSubItem: {
          paddingLeft: "16px",
          marginBottom: "5px",
        },
        amount: {
          fontSize: "14px",
          fontWeight: 900,
        },
        divider: {
          width: "100%",
          borderTop: "0.5px solid #D9D9D9",
          marginTop: "8px",
          marginBottom: "8px",
        },
        totalAmountTitle: {
          color: "#4f46e5",
          fontSize: "13px",
          fontWeight: 900,
        },
        bundlePrice: {
          color: "#4f46e5",
          fontSize: "13px",
          fontWeight: 900,
        },
        installmentsInfoContainer: {
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          justifyContent: "flex-start",
          alignItems: "flex-start",
          width: "100%",
        },
        installmentsInfoItem: {
          color: "#4f46e5",
          fontSize: "11px",
        },
        bundlePriceContainerTitle: {
          display: "flex",
          flexDirection: "row",
          width: "100%",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: "16px",
        },
        bundlePriceTitle: {
          color: "#000",
          fontSize: "12px",
          fontWeight: 600,
          marginBottom: "6px",
        },
        bundlePriceContainer: {
          display: "flex",
          alignItems: "center",
          marginBottom: "16px",
          paddingBottom: "16px",
          borderBottom: "1px solid #D9D9D9",
        },
        footer: {
          height: "112px",
          backgroundColor: "#fff",
          padding: "8px 0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
        },
        footerText: {
          fontSize: "12px",
          marginBottom: "8px",
          color: "#9DA5B1",
        },
        footerImage: {
          height: "40px",
          marginBottom: "8px",
          width: "80px",
        },
      }),
    []
  );

  useEffect(() => {
    Font.register({
      family: "Lato",
      src: "https://fonts.gstatic.com/s/lato/v24/S6u9w4BMUTPHh7USSwaPGQ3q5d0N7w.woff2",
    });
  }, []);

  const { id } = useParams();
  const { data, isLoading } = useGetPublicOrderQuery(id || "", {
    skip: !!order,
    refetchOnMountOrArgChange: true,
  });
  const { userInfo } = useUserWithAccount();
  const { getClearPriceByOrderType } = usePrice();
  const { accountInfo } = useSelector(orderSelector);
  const query = useQuery();
  const internalUser = query.get("internalUser");
  const navigate = useNavigate();
  const { isMobile } = useIsMobile();

  const orderInfo = useMemo(() => {
    if (order) {
      return order;
    }

    return data?.data || ({} as IOrder);
  }, [data, order]);

  const isVersion2Receipt = useMemo(() => {
    if (!orderInfo) return false;
    const { customizes } = orderInfo?.account || {};
    const version2Receipt = customizes?.find(
      (customize: any) => customize.orderType === orderTypeConstants.GFE
    )?.receiptVersion?.version2Receipt;
    return version2Receipt && orderInfo.orderType === orderTypeConstants.GFE;
  }, [orderInfo]);

  const computedLogoUrl = useMemo(() => {
    return (
      accountInfo?.accountLogoUrl ||
      orderInfo?.account?.accountLogoUrl ||
      hospitalGeneralDefault
    );
  }, [accountInfo, orderInfo]);

  const clearFee =
    orderInfo?.account?.accountFees.find(
      (fee: any) => fee.type === getFeeTypeForOrder(orderInfo.orderType)
    )?.valueInPercentage || 0;

  const { clearPrice, total, totalSaving } = getClearPriceByOrderType({
    order: orderInfo,
  });

  const isPatientResponsibility =
    orderInfo?.orderType === orderTypeConstants.PATIENT_RESPONSIBILITY;

  const isClearEstimate = orderInfo?.orderType === orderTypeConstants.GFE;

  const clearCharge = calculateClearCharge(clearPrice, Number(clearFee));

  const paymentInfoFull = useMemo(() => {
    if (!data && !order) return {};
    const allPaidPayments =
      orderInfo?.payments.filter((aPayment) => aPayment.status === "Paid") ||
      [];
    const firstPayment = allPaidPayments[0];
    const sortedPayments = allPaidPayments.sort((a, b) => {
      const dateA = new Date(a.createdAt || new Date(0)).getTime();
      const dateB = new Date(b.createdAt || new Date(0)).getTime();
      return dateA - dateB;
    });
    const secondPayment = sortedPayments[1];
    return {
      firstPayment,
      secondPayment,
    };
  }, [orderInfo, data, order]);

  const uniqueProcedureList = useMemo(() => {
    if (isEmpty(orderInfo)) return [];

    const { procedures } = orderInfo.serviceDetails;

    const uniqueArray = [] as unknown as UniqueProcedureList[];
    const seenValues = new Set();

    procedures?.forEach((item) => {
      const valueToCheck = item.code;

      if (!seenValues.has(valueToCheck)) {
        seenValues.add(valueToCheck);
        uniqueArray.push(item);
      }
    });

    return uniqueArray;
  }, [orderInfo.serviceDetails]);

  const providersListForBundledPricing = useMemo(() => {
    if (isEmpty(orderInfo)) return [];

    const { procedures } = orderInfo?.serviceDetails as IDefaultServiceDetails;

    const proceduresParsed: {
      providerName: string;
      amountInCents: number;
    }[] = [];
    const providerMap = new Map();

    // eslint-disable-next-line no-restricted-syntax
    for (const item of procedures) {
      const { providerName, amountInCents } = item;

      if (providerMap.has(providerName)) {
        providerMap.set(
          providerName,
          providerMap.get(providerName) + amountInCents
        );
      } else {
        providerMap.set(providerName, amountInCents);
      }
    }

    providerMap.forEach((amountInCents, providerName) => {
      proceduresParsed.push({ providerName, amountInCents });
    });

    return proceduresParsed;
  }, [orderInfo.serviceDetails]);

  const providersListForGfePricing = useMemo(() => {
    if (isEmpty(orderInfo)) return [];
    const bundledProviderAmounts =
      orderInfo?.paymentFullBreakdown?.bundledProviderAmounts || [];
    const providerAmountsParsed: {
      providerName: string;
      amountInCents: number;
    }[] = [];
    const providerMap = new Map();
    // eslint-disable-next-line no-restricted-syntax
    for (const item of bundledProviderAmounts) {
      const { providerName, expectedTransferAmount } = item;

      if (providerMap.has(providerName)) {
        providerMap.set(
          providerName,
          providerMap.get(providerName) + expectedTransferAmount
        );
      } else {
        providerMap.set(providerName, expectedTransferAmount);
      }
    }

    providerMap.forEach((amountInCents, providerName) => {
      providerAmountsParsed.push({ providerName, amountInCents });
    });

    return providerAmountsParsed;
  }, [orderInfo?.paymentFullBreakdown]);

  const providersList = useMemo(() => {
    if (isClearEstimate) {
      return providersListForGfePricing;
    }
    return providersListForBundledPricing;
  }, [
    isClearEstimate,
    providersListForGfePricing,
    providersListForBundledPricing,
  ]);

  const clearChargeBundledOrder = parsePrice(
    centsToDollars(
      clearPrice - sumBy(providersListForBundledPricing, "amountInCents")
    )
  );

  const isFinancingPaymentMethod =
    paymentInfoFull?.firstPayment?.type === "Financing";

  const showPaymentPlanInfo = useMemo(() => {
    if (!data && !order) return false;
    const astivePaymentPlan = orderInfo.paymentPlan.find((plan) => plan.active);
    return !!astivePaymentPlan?.installments?.length;
  }, [orderInfo, data, order]);

  const paymentPlanInfo = useMemo(() => {
    if (!data && !order) return null;
    const astivePaymentPlan = [...orderInfo.paymentPlan].find(
      (plan) => plan.active
    );
    const allPaidPayments =
      orderInfo?.payments.filter((aPayment) => aPayment.status === "Paid") ||
      [];
    const sortedPayments = allPaidPayments.sort((a, b) => {
      const dateA = new Date(a.createdAt || new Date(0)).getTime();
      const dateB = new Date(b.createdAt || new Date(0)).getTime();
      return dateA - dateB;
    });
    const secondPayment = sortedPayments[1];
    const showInstallmentsInfo =
      astivePaymentPlan?.remainingAmountInCents &&
      astivePaymentPlan.paymentPlanDuration;

    const lastPayment = sortedPayments[sortedPayments.length - 1];
    const installments = astivePaymentPlan?.installments || [];
    const sortedInstallments = [...installments].sort((a, b) => {
      return new Date(a.dateDue).getTime() - new Date(b.dateDue).getTime();
    });
    const firstNullPaymentIdInstallment = sortedInstallments.find(
      (installment) => !installment.paymentId
    );
    const nextInstallmentDate = firstNullPaymentIdInstallment?.dateDue
      ? parseDate(firstNullPaymentIdInstallment.dateDue, false, true)
      : "N/A";

    const remainingInstallmentsCount = sortedInstallments.filter(
      (installment) => !installment.paymentId
    ).length;

    return {
      ...astivePaymentPlan,
      lastPayment,
      showInstallmentsInfo,
      nextInstallmentDate,
      remainingInstallmentsCount,
      secondPayment,
    };
  }, [orderInfo, data, order]);

  const stripeMethodId = useMemo<string | null>(() => {
    if (!showPaymentPlanInfo) {
      return paymentInfoFull?.firstPayment?.stripePaymentMethodId || null;
    }
    return paymentPlanInfo?.stripePaymentMethodId || null;
  }, [showPaymentPlanInfo, paymentInfoFull, paymentPlanInfo]);

  const secondCardStripeMethodId = useMemo<string | null>(() => {
    if (!showPaymentPlanInfo) {
      return paymentInfoFull?.secondPayment?.stripePaymentMethodId || null;
    }
    if (paymentPlanInfo?.secondPayment?.splitTotalPaymentId) {
      return paymentPlanInfo?.secondPayment?.stripePaymentMethodId || null;
    }
    return null;
  }, [showPaymentPlanInfo, paymentInfoFull, paymentPlanInfo]);

  const { data: cardData, isFetching: isFetchingCard } =
    useGetCardLastDigitsQuery(stripeMethodId, {
      skip: !stripeMethodId,
    });
  const { data: secondCardData, isFetching: isFetchingSecondCard } =
    useGetCardLastDigitsQuery(secondCardStripeMethodId, {
      skip: !secondCardStripeMethodId,
    });

  const paymentsInfo = useMemo(() => {
    const allPayments = orderInfo?.payments || [];
    const sortedAllPayments = [...allPayments].sort((a, b) => {
      const dateA = new Date(a.createdAt || new Date(0)).getTime();
      const dateB = new Date(b.createdAt || new Date(0)).getTime();
      return dateA - dateB;
    });
    const allPaidPayments = sortedAllPayments.filter(
      (p) => p.status === "Paid"
    );
    const allRefundedPayments = sortedAllPayments.filter(
      (p) => p.status === "Refunded"
    );
    const totalPaid = allPaidPayments.reduce(
      (sum, aPayment) => sum + (aPayment.amountInCents || 0),
      0
    );
    const totalRefunded = allRefundedPayments.reduce(
      (sum, aPayment) => sum + (aPayment.amountInCents || 0),
      0
    );
    const isTotalAdjusted = !!allPayments.find((p) => p.status === "Refunded");
    const totalPaidAmount =
      allPayments.reduce(
        (sum, aPayment) => sum + (aPayment.amountInCents || 0),
        0
      ) || 0;
    return {
      sortedAllPayments,
      isTotalAdjusted,
      totalPaidAmount,
      allPaidPayments,
      allRefundedPayments,
      totalPaid,
      totalRefunded,
    };
  }, [orderInfo]);

  const insuranceInfo = useMemo(() => {
    if (orderInfo.orderType !== orderTypeConstants.GFE) {
      return null;
    }
    const payerName = orderInfo?.payer?.name;
    const secondaryPayerName = orderInfo?.secondaryPayer?.name;
    return secondaryPayerName
      ? `${payerName}, ${secondaryPayerName}`
      : payerName;
  }, [orderInfo]);

  const isCreatedWithDirectPay = useMemo(() => {
    if (!orderInfo) return false;
    return orderInfo?.orderHistories?.some(
      (history) => history.type === "CreatedWithDirectPay"
    );
  }, [orderInfo]);

  const breakdownInfo = useMemo(() => {
    if (!orderInfo) return null;
    if (orderInfo.orderType !== orderTypeConstants.GFE) return null;
    const { paymentFullBreakdown, paymentPlanBreakdown } = orderInfo;
    const isFullyPaidWithoutPaymentPlan =
      orderInfo.status === orderStatusConstants.PAID &&
      !orderInfo?.paymentPlanBreakdown;
    const transfers = orderInfo.orderHistories.filter(
      (history) => history.type === "Transfer"
    );

    // Helper function to calculate net transfers for a provider
    const calculateNetTransfers = (providerId: string) => {
      let netTransfers = 0;
      transfers.forEach((transfer) => {
        if (transfer.toProviderId === providerId) {
          // Money received
          netTransfers += transfer.amountInCents || 0;
        }
        if (transfer.fromProviderId === providerId) {
          // Money sent
          netTransfers -= transfer.amountInCents || 0;
        }
      });
      return netTransfers;
    };
    if (isFullyPaidWithoutPaymentPlan) {
      const bundledProviderAmounts =
        paymentFullBreakdown?.bundledProviderAmounts || [];

      return bundledProviderAmounts.map((provider) => {
        const transfers = calculateNetTransfers(provider.providerId);
        const totalAmountPaid = provider.clearDiscountedPrice;
        return {
          providerId: provider.providerId,
          providerName: provider.providerName,
          totalAmountPaid,
          lastPaymentAmount: provider.clearDiscountedPrice,
          lastPaymentDate: "",
          transfers,
          finalBalance: totalAmountPaid + transfers,
        };
      });
    }

    // Handle payment plan scenarios
    const payments = paymentPlanBreakdown?.payments
      ? normalizePaymentPlanBreakdown(paymentPlanBreakdown.payments)
      : undefined;
    if (!payments || !payments.length) return [];

    // Get payments that should be included in the calculation
    const paidPayments = payments.filter((payment, index) => {
      // First payment is always included
      if (index === 0) return true;
      // Other payments only if they have been paid (paymentId exists)
      return payment.installmentInDb?.paymentId !== null;
    });

    // Calculate total paid amount per provider
    const providerPaidAmounts = new Map<
      string,
      {
        providerId: string;
        providerName: string;
        totalAmountPaid: number;
        lastPaymentAmount: number;
        lastPaymentDate: string;
        transfers: number;
        finalBalance: number;
      }
    >();

    paidPayments.forEach((payment) => {
      payment.providerBreakdown.forEach((providerPayment: any) => {
        const { providerId } = providerPayment;
        const existing = providerPaidAmounts.get(providerId);

        const paymentAmount = providerPayment.clearDiscountedPrice || 0;
        const lastPaymentAmount = providerPayment.clearDiscountedPrice || 0;

        if (existing) {
          existing.totalAmountPaid += paymentAmount;
          existing.lastPaymentAmount = lastPaymentAmount;
          existing.lastPaymentDate = payment.dateDue;
          existing.finalBalance = existing.totalAmountPaid + existing.transfers;
        } else {
          const transfers = calculateNetTransfers(providerId);
          const totalAmountPaid = paymentAmount;
          providerPaidAmounts.set(providerId, {
            providerId,
            providerName: providerPayment.providerName,
            totalAmountPaid,
            lastPaymentAmount,
            transfers,
            finalBalance: totalAmountPaid + transfers,
            lastPaymentDate: payment.dateDue,
          });
        }
      });
    });

    // Calculate full amounts for each provider by summing across ALL payments
    const providerFullAmounts = new Map<string, number>();

    payments.forEach((payment) => {
      payment.providerBreakdown.forEach((providerPayment: any) => {
        const { providerId } = providerPayment;
        const paymentAmount = providerPayment.clearDiscountedPrice || 0;
        const existing = providerFullAmounts.get(providerId) || 0;
        providerFullAmounts.set(providerId, existing + paymentAmount);
      });
    });

    // Create provider breakdown with cumulative paid amounts and full amounts
    const providerFinalInfo = Array.from(providerPaidAmounts.values()).map(
      (provider) => {
        const fullAmount = providerFullAmounts.get(provider.providerId) || 0;
        return {
          ...provider,
          clearDiscountedPriceFull: fullAmount,
        };
      }
    );

    return providerFinalInfo;
  }, [orderInfo]);

  const prices = () => {
    if (!isFinancingPaymentMethod) {
      return isPatientResponsibility ? (
        <View style={styles.bundlePriceContainerTitle}>
          <Text style={styles.bundlePriceTitle}>{orderInfo?.account.name}</Text>

          <Text style={styles.bundlePrice}>
            {parsePrice(centsToDollars(clearPrice - clearCharge))}
          </Text>
        </View>
      ) : (
        providersList.map((provider, index) => (
          <View
            style={styles.bundlePriceContainerTitle}
            key={`${provider.providerName}_${index}`}
          >
            <Text style={styles.bundlePriceTitle}>{provider.providerName}</Text>

            <Text style={styles.bundlePrice}>
              {parsePrice(centsToDollars(provider.amountInCents))}
            </Text>
          </View>
        ))
      );
    }
    return null;
  };

  const waitingOnCardOne = !!stripeMethodId && (isFetchingCard || !cardData);
  const waitingOnCardTwo =
    !!secondCardStripeMethodId && (isFetchingSecondCard || !secondCardData);
  const readyToRender =
    !isLoading && !!orderInfo && !waitingOnCardOne && !waitingOnCardTwo;

  if (!readyToRender) return <LoadingSpinner />;

  const receiptDocument = (
    <ReceiptDefault
      styles={styles}
      logoUrl={computedLogoUrl}
      orderInfo={orderInfo}
      uniqueProcedureList={uniqueProcedureList}
      paymentsInfo={paymentsInfo}
      showPaymentPlanInfo={showPaymentPlanInfo}
      paymentPlanInfo={paymentPlanInfo}
      paymentInfoFull={paymentInfoFull}
      cardData={cardData}
      secondCardData={secondCardData}
      clearLogo={clearLogo}
      isVersion2={isVersion2Receipt}
      insuranceInfo={insuranceInfo}
      breakDownInfo={breakdownInfo}
      isCreatedWithDirectPay={isCreatedWithDirectPay}
    />
  );

  return (
    <>
      {(internalUser || !!userInfo) && showBackToOrderButton && (
        <StyledButton
          icon="pi pi-arrow-left"
          onClick={() => {
            navigate(`/orders/${orderInfo.id}`);
          }}
          className="p-button-text p-button-secondary p-0 pt-3 pl-3"
        >
          <p className="small-text">Back to Order</p>
        </StyledButton>
      )}
      {isMobile ? (
        <MobileReceiptViewer receiptDocument={receiptDocument} />
      ) : (
        <PDFViewer width="100%" height="1200px">
          {receiptDocument}
        </PDFViewer>
      )}
    </>
  );
}
